/**
 * GET /v1/schools/:schoolId/messageable-section-parents
 *
 * The authenticated caller's CONTEXTUAL section-parent id set at the active
 * school: the ids of the parents who share at least one section with the caller
 * (any offering type — academics, advisories, athletics, community groups).
 * Session-derived (no client-supplied user id), schoolId-scoped, rate-limited.
 * Feeds the mobile new-message recipient picker's `section_parent` category
 * (plan 2026-07-21-002, U3 → U4).
 *
 * Response is the id set ONLY — no names, roles, or roster metadata
 * (min-necessary-data). The overlay only ADDS visibility to people already in
 * the picker's `getSchoolUsers` candidate list, so no PII beyond ids is needed.
 * Keeping it id-only is what stops the parent-to-parent roster graph from
 * becoming an enumeration or PII surface (R12).
 *
 * ## Id-space (LOAD-BEARING)
 *
 * `sectionParentSchoolUserIds` holds `UsersOnSchools.publicId` CUIDs — EXACTLY
 * the field the mobile picker keys `candidate.id` on (see the
 * messageable-moderators contract for the full reasoning). Emitting internal
 * `UsersOnSchools.id` row ids instead would silently match NOBODY.
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-07-21-002-feat-parent-chat-roster-scoping-plan.md (U2, U4)
 */

import { z } from 'zod'

/**
 * Messageable-section-parents response — the caller's contextual shared-roster
 * parent id set. `sectionParentSchoolUserIds` are `UsersOnSchools.publicId`
 * CUIDs (the same id the `getSchoolUsers` picker candidates carry as `id`).
 * Deduped; excludes the caller's own id.
 */
export const MessageableSectionParentsResponseSchema = z.object({
  sectionParentSchoolUserIds: z.array(z.string())
})
export type MessageableSectionParentsResponse = z.infer<typeof MessageableSectionParentsResponseSchema>
